/**
 * @fileoverview My Page badge variants.
 * @description The Badge colour for each target display status and priority, shared by the My Page
 * table and its details modal so a target reads the same in both places.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type {
  MyPageDisplayStatus,
  MyPagePriority,
  MyPageStageCode,
} from '@/features/sales/types/my-page.types'

/** @description A Badge colour. */
export type MyPageBadgeVariant = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

/** @description The Badge colour of each display status. */
export const DISPLAY_STATUS_VARIANT: Record<MyPageDisplayStatus, MyPageBadgeVariant> = {
  ON_TRACK: 'success',
  AT_RISK: 'warning',
  OVERDUE: 'danger',
  AWAITING_PAYMENT: 'info',
  COMPLETED: 'success',
  CLOSED: 'neutral',
}

/** @description The Badge colour of each priority. */
export const PRIORITY_VARIANT: Record<MyPagePriority, MyPageBadgeVariant> = {
  HIGH: 'danger',
  MEDIUM: 'warning',
  LOW: 'neutral',
}

/**
 * @description The coloured bar at the left of a My Page row, one colour per display status — the
 * same colours the achievement progress bar and its legend use.
 */
export const DISPLAY_STATUS_BAR_CLASS: Record<MyPageDisplayStatus, string> = {
  ON_TRACK: 'bg-track',
  AT_RISK: 'bg-status-warning',
  OVERDUE: 'bg-danger',
  AWAITING_PAYMENT: 'bg-info',
  COMPLETED: 'bg-teal',
  CLOSED: 'bg-table-header',
}

/** @description The chip colours for each stage, as the My Page table's Current Status shows it. */
export const STAGE_CHIP_CLASS: Record<MyPageStageCode, string> = {
  NEW: 'bg-surface-muted text-slate',
  CONTACTED: 'bg-info-bg text-info',
  DEMO_REQUESTED: 'bg-success-bg text-success',
  PROPOSAL_SENT: 'bg-status-warning-bg text-status-warning',
  UNDER_NEGOTIATION: 'bg-purple-bg text-purple',
  SUBSCRIPTION_REQUESTED: 'bg-info-soft-bg text-accent-panel',
  LIVE: 'bg-success-bg text-success',
  LOST: 'bg-danger-bg text-danger',
  ON_HOLD: 'bg-surface-muted text-slate',
  REJECTED: 'bg-danger-bg text-danger',
}

/**
 * @description Formats an ISO date-time as "yyyy-mm-dd hh:mm".
 * @param {string} iso - The ISO date-time.
 * @returns {string} The formatted date and time.
 */
export function formatMyPageDateTime(iso: string): string {
  return iso.slice(0, 16).replace('T', ' ')
}
